import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";

export type FieldType = "text" | "email" | "password" | "select" | "textarea";

export type FieldOption = {
  label: string;
  value: string;
};

export type FieldConfig = {
  name: string;
  label: string;
  type: FieldType;
  options?: FieldOption[]; // Only for type "select"
  defaultValue?: string;
  placeholder?: string;
  description?: string;
};

export type BaseFormProps<T extends z.ZodObject<any>> = {
  schema: T;
  fields: FieldConfig[];
  defaultValues?: Partial<z.infer<T>>;
  onSubmit: (values: z.infer<T>) => void | Promise<void>;
  submitLabel?: string;
  notes?: string;
  isLoading?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
};

export function BaseForm<T extends z.ZodObject<any>>({
  schema,
  fields,
  defaultValues,
  onSubmit,
  submitLabel = "Submit",
  notes,
  isLoading = false,
  onCancel,
  cancelLabel = "Cancel",
}: BaseFormProps<T>) {
  type FormValues = z.infer<T>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: (defaultValues || {}) as any,
    mode: "onTouched",
  });

  const handleSubmit = async (values: FormValues) => {
    await onSubmit(values as z.infer<T>);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="w-full space-y-5 rounded-xl border border-border bg-card p-5 shadow-sm"
      >
        {fields.map((field) => {
          if (field.type === "select") {
            const options = field.options;
            if (!options) {
              return null;
            }
            return (
              <FormField
                key={field.name}
                control={form.control as any}
                name={field.name as any}
                render={({ field: formField }) => (
                  <FormItem>
                    <FormLabel>{field.label}</FormLabel>
                    <Select
                      value={formField.value || field.defaultValue || ""}
                      onValueChange={(value) => formField.onChange(value)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={field.placeholder || "Select..."} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.description && (
                      <FormDescription>{field.description}</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            );
          }

          if (field.type === "textarea") {
            return (
              <FormField
                key={field.name}
                control={form.control as any}
                name={field.name as any}
                render={({ field: formField }) => (
                  <FormItem>
                    <FormLabel>{field.label}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={field.placeholder}
                        {...formField}
                        value={formField.value || field.defaultValue || ""}
                      />
                    </FormControl>
                    {field.description && (
                      <FormDescription>{field.description}</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            );
          }

          // text, email, password (renders Input)
          return (
            <FormField
              key={field.name}
              control={form.control as any}
              name={field.name as any}
              render={({ field: formField }) => (
                <FormItem>
                  <FormLabel>{field.label}</FormLabel>
                  <FormControl>
                    <Input
                      type={field.type}
                      placeholder={field.placeholder}
                      autoComplete="off"
                      {...formField}
                      value={String(formField.value || field.defaultValue || "")}
                    />
                  </FormControl>
                  {field.description && (
                    <FormDescription>{field.description}</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        })}

        {notes && (
          <div className="rounded-md border border-border bg-muted/60 p-3 text-sm text-muted-foreground">
            {notes}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Submitting..." : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}

